import { BookingCreateCarRecord, CarWheelIdentifiers, StuddedTireSeasonOverrideReason } from '@navio/nsdk';
import { isAxiosError } from 'noddi-util';

/**
 * Booking creation refuses an out-of-season studded-tire booking with this code. It is a confirmation request, not a
 * block: re-post the same payload with an `is_studded` wheel identifier override and a reason on each car.
 */
const STUDDED_TIRE_SEASON_OVERRIDE_REQUIRED_CODE = 'booking_studded_tire_season_override_required';

/**
 * The backend's localized explanation of the studded-tire season rule when creation was refused for it, else null.
 * Shown as-is: it names the legal window, the booked date and the regulation. Takes `unknown` so the mutation cache
 * can ask before the error is narrowed.
 */
export const getStuddedTireSeasonExplanation = (error: unknown): string | null => {
  const data: unknown = isAxiosError(error) ? error.response?.data : undefined;
  if (typeof data !== 'object' || data === null || !('errors' in data) || !Array.isArray(data.errors)) {
    return null;
  }
  const items: unknown[] = data.errors;
  for (const item of items) {
    if (
      typeof item === 'object' &&
      item !== null &&
      'code' in item &&
      item.code === STUDDED_TIRE_SEASON_OVERRIDE_REQUIRED_CODE &&
      'detail' in item &&
      typeof item.detail === 'string'
    ) {
      return item.detail;
    }
  }
  return null;
};

type CarsPayload = {
  cars: BookingCreateCarRecord[];
};

/** The refused payload, re-posted with an is_studded approval and reason on every car. */
export const withStuddedTireSeasonOverride = <T extends CarsPayload>(
  payload: T,
  reason: StuddedTireSeasonOverrideReason
): T => ({
  ...payload,
  cars: payload.cars.map((car) => ({
    ...car,
    wheelIdentifierOverrides: [
      ...(car.wheelIdentifierOverrides ?? []).filter((item) => item.identifier !== CarWheelIdentifiers.IS_STUDDED),
      { identifier: CarWheelIdentifiers.IS_STUDDED, reason }
    ]
  }))
});
